import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { SearchDialog } from './SearchDialog';
import { DeployedUrlButtons } from './DeployedUrlButtons';
import { QuickNavButtons } from './QuickNavButtons';
import type { LayoutProps } from '../types';

export function Layout({
  siteName,
  logo,
  navigation,
  children,
  deployedUrl,
  enableSort,
  defaultSortField,
  sortStorageKey,
  quickNav,
  enableSearch,
}: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // Global Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    if (!enableSearch) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [enableSearch]);

  const brandContent = logo || siteName;

  return (
    <div className="min-h-screen bg-white dark:bg-[var(--color-site-bg,#1f2937)]">
      {/* Mobile header */}
      <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-gray-200 bg-white px-4 dark:border-[var(--color-site-border,#374151)] dark:bg-[var(--color-site-bg,#1f2937)] lg:hidden">
        <button
          onClick={() => setSidebarOpen(true)}
          className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
          <span className="sr-only">Open sidebar</span>
        </button>
        <Link
          to="/"
          className="flex-1 text-lg font-semibold text-docs-600 dark:text-docs-400"
        >
          {brandContent}
        </Link>
        {enableSearch && (
          <button
            onClick={() => setSearchOpen(true)}
            className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <span className="sr-only">Search</span>
          </button>
        )}
        {quickNav && quickNav.length > 0 && (
          <QuickNavButtons items={quickNav} />
        )}
        {deployedUrl && <DeployedUrlButtons deployedUrl={deployedUrl} />}
      </header>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-50 bg-gray-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 transform flex-col border-r border-gray-200 bg-gray-50 transition-transform duration-200 ease-in-out dark:border-[var(--color-site-border,#374151)] dark:bg-[var(--color-site-bg-alt,#111827)] lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Sidebar header */}
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4 dark:border-[var(--color-site-border,#374151)]">
          <Link
            to="/"
            className="text-lg font-semibold text-docs-600 dark:text-docs-400"
          >
            {brandContent}
          </Link>
          <div className="flex items-center gap-1">
            {deployedUrl && (
              <div className="hidden lg:flex">
                <DeployedUrlButtons deployedUrl={deployedUrl} />
              </div>
            )}
            <button
              onClick={() => setSidebarOpen(false)}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50 lg:hidden"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <span className="sr-only">Close sidebar</span>
            </button>
          </div>
        </div>

        {/* Quick nav buttons */}
        {quickNav && quickNav.length > 0 && (
          <div className="shrink-0 border-b border-gray-200 px-4 py-2 dark:border-[var(--color-site-border,#374151)]">
            <QuickNavButtons items={quickNav} />
          </div>
        )}

        {/* Search button in sidebar */}
        {enableSearch && (
          <div className="shrink-0 border-b border-gray-200 px-4 py-2 dark:border-[var(--color-site-border,#374151)]">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-500 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-400"
            >
              <svg
                className="h-4 w-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <span className="flex-1 text-left">Search...</span>
              <kbd className="hidden rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-xs font-medium text-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-500 sm:inline-block">
                {typeof navigator !== 'undefined' &&
                /Mac/.test(navigator.platform)
                  ? '\u2318'
                  : 'Ctrl+'}
                K
              </kbd>
            </button>
          </div>
        )}

        {/* Sidebar content */}
        <div className="sidebar-scroll min-h-0 flex-1 overflow-y-auto p-4">
          <Sidebar
            navigation={navigation}
            onNavigate={() => setSidebarOpen(false)}
            enableSort={enableSort}
            defaultSortField={defaultSortField}
            sortStorageKey={sortStorageKey}
          />
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:pl-72">
        <div className="mx-auto max-w-4xl px-4 py-8 lg:px-8">{children}</div>
      </main>

      {/* Search dialog */}
      {enableSearch && (
        <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
      )}
    </div>
  );
}
